f
<template>
  <div>
    <!-- Search input -->
    <h1 class="mt-10 mb-0 text-lg md:text-2xl md:mb-4">{{ translations.search.pageHeading }}</h1>
    <form @submit.prevent="handleSearchSubmit" role="search" name="searchresultform">
      <!-- Search input -->
      <div class="mb-4 vue-search-input">
        <ellevio-web-search-result-input
          @submitSearch="handleSearchSubmit"
          :placeholderText="translations.search.placeholderText"
          :label="translations.search.inputLabel"
          value=""
          :inputTextModel="inputText"
          :searchTooltipLabel="translations.search.searchButtonTooltip"
          :apiUrl="typeAheadUrl"
          :numberOfItems="translations.search.numberOfItems"
          :clearSearchFieldLabel="translations.search.clearSearchFieldLabel"
          :searchNoHitsContent="translations.search.searchNoHitsContent" />
      </div>

      <!-- Category filter buttons -->
      <div v-if="categories?.length > 0 && !noSearchResultsMessage" class="mt-6 border-t-2 border-newColors-gray-background md:pt-10 md:mt-7">
        <h2 class="relative mb-4 text-lg md:text-2xl">{{ translations.search.categoryHeading }} {{ queryParam && `"${queryParam}"` }}<span class="absolute text-transparent -top-5" style="width: 1px; height: 1px"></span></h2>
        <div class="max-w-2xl mb-2 btn-group-tight" data-testid="searchCategoryFilterButtonContainer">
          <button v-for="(category, index) in categories" :key="category.id" type="button" @click="handleCategoryChange(category)" :class="['btn-filter', { 'btn-filter-active': category.isSelected }]" :data-testid="`searchCategoryFilterButton-${index}`">
            {{ category.name }} ({{ category.count }})
          </button>
        </div>
      </div>

      <!-- Sorting options -->
      <section v-show="categories?.length > 0 && !noSearchResultsMessage" class="flex flex-col-reverse items-baseline justify-between gap-4 mb-4 sm:gap-0 md:items-center sm:flex-row">
        <!-- Search results -->
        <div class="text-lg md:text-xl shrink-0">{{ totalHits }} {{ translations.search.searchResults }}</div>

        <!-- Sorting -->
        <div class="flex items-center pt-4 text-xl md:justify-end md:w-full font-futuraEfBook sm:pt-0">
          <label for="sorting" class="pr-2">{{ translations.search.sorting }}:</label>
          <span class="select-wrapper">
            <select name="sorting" id="sorting" class="rounded-sm no-border select-element focus:outline-none focus-visible:outline-black-small" v-model="selectedSortingOption" @change="handleSortingChange" data-testid="searchPageSortSelect">
              <option :value="option.value" v-for="option in sortingOptions" :key="option.text">{{ option.text }}</option>
            </select>
          </span>
        </div>
      </section>
    </form>

    <!-- Alert -->
    <ellevio-web-alert v-if="noSearchResultsMessage" class="max-w-3xl" type="softWarning" :rounded-borders="true" :title="noSearchResultsMessage.heading" :text="noSearchResultsMessage.text" />

    <!-- Search results -->
    <div v-if="!noSearchResultsMessage">
      <ellevio-web-show-more-items
        :show-items-count="false"
        :additional-items="additionalItemsCount"
        :type-description="translations.search.searchResults"
        :total-items="activeCategory?.count"
        :type="'searchResult'"
        :api-url="apiUrl"
        :api-params="showMoreParams"
        :newHeaderLayout="true">
        <ellevio-web-search-result-item
          v-for="searchHit in searchHits"
          :key="searchHit.url"
          :newHeaderLayout="true"
          :heading="searchHit.title"
          :excerpt="searchHit.excerpt"
          :url="searchHit.url"
          :breadcrumbs="searchHit.breadCrumb"
          :date="searchHit.publishDate"
          :date-text="getDateText(searchHit.publishDate)" />
      </ellevio-web-show-more-items>
    </div>
  </div>
</template>

<script>
import EllevioWebSearchResultInput from '../vue-components/SearchResultInput.vue'
import EllevioWebSearchResultItem from '../structure/SearchResultItem.vue'
import EllevioWebShowMoreItems from '../vue-components/ShowMoreItems.vue'
import EllevioWebAlert from '../vue-components/Alert.vue'

import { getBaseUrl } from '../../../helpers/utils.js'

import $ from 'jquery'
import axios from 'axios'
import { initForms } from '../../../scripts/forms'

export default {
  name: 'EllevioWebSearchResults',
  components: {
    EllevioWebSearchResultInput,
    EllevioWebSearchResultItem,
    EllevioWebShowMoreItems,
    EllevioWebAlert
  },
  props: {
    translations: {
      type: Object,
      default: () => ({})
    },
    apiUrl: {
      type: String,
      required: true
    },
    typeAheadUrl: {
      type: String,
      required: true
    },
    getMoreSearchItemsUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      queryParam: '',
      sortByParam: '',
      filterParam: '',
      inputText: '',
      loadMoreTypeDescription: 'sökresultat',
      additionalItemsCount: 10,
      sortingOptions: [],
      selectedSortingOption: '',
      searchHits: [],
      totalHits: 0,
      categories: [],
      activeCategory: {},
      noSearchResultsMessage: false
    }
  },
  mounted() {
    this.$nextTick(function () {
      initForms($)
      this.fetchSearchResults()
    })
  },
  computed: {
    baseUrl() {
      return getBaseUrl()
    },
    showMoreParams() {
      this.getUrlParams()
      return `?query=${this.queryParam}` + (this.filterParam ? `&filter=${this.filterParam}` : '') + (this.sortByParam ? `&sortBy=${this.sortByParam}` : '')
    }
  },
  methods: {
    fetchSearchResults(searchStringFromInputField) {
      this.getUrlParams()
      const searchString = searchStringFromInputField || this.queryParam

      axios
        .get(`${this.apiUrl}?query=${searchString}` + (this.filterParam ? `&filter=${this.filterParam}` : '') + (this.sortByParam ? `&sortBy=${this.sortByParam}` : ''))
        .then((response) => {
          const {
            sortingOptions,
            result: { filters, hits, totalHits },
            noResultsError
          } = response.data.data

          this.categories = filters
          this.sortingOptions = sortingOptions
          this.searchHits = hits
          this.totalHits = totalHits
          this.activeCategory = this.categories.find((category) => category.isSelected)
          this.selectedSortingOption = this.sortingOptions.find((option) => option.isSelected).value

          // Since these values have a 'isSelected' prop in the API response, we need to make sure the url params match the response.
          this.addCategoryToUrl(this.activeCategory?.name)
          this.addSortingToUrl(this.selectedSortingOption)

          // Handle error message for search result
          // if (noResultsError) {
          if (noResultsError?.heading !== '' && noResultsError?.text !== '') {
            this.noSearchResultsMessage = { heading: noResultsError.heading, text: noResultsError.text }
          }
        })
        .catch((error) => {
          console.error('Error:', error)
          this.noSearchResultsMessage = { heading: this.translations.search.errorMessages.fetchError.heading, text: this.translations.search.errorMessages.fetchError.text }
        })
    },
    getDateText(date) {
      const dateObj = new Date(date)
      return dateObj.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    getUrlParams() {
      const urlParams = new URLSearchParams(window.location.search)
      this.queryParam = urlParams.get('query') || ''
      this.sortByParam = urlParams.get('sortBy') || ''
      this.filterParam = urlParams.get('filter') || ''

      if (this.queryParam) {
        // Update search input field text with query from url params.
        this.inputText = this.queryParam
      }
    },
    addCategoryToUrl(category) {
      const params = new URLSearchParams(window.location.search)
      params.set('filter', category)
      window.history.replaceState({}, document.title, `${location.pathname}?${params}`)
    },
    addSearchQueryToUrl(query) {
      const params = new URLSearchParams(window.location.search)

      if (!query) {
        // Remove the query param if empty
        params.delete('query')
      } else {
        params.set('query', query)
      }

      window.history.replaceState({}, document.title, `${location.pathname}?${params}`)
    },
    addSortingToUrl(sorting) {
      const params = new URLSearchParams(window.location.search)
      params.set('sortBy', sorting)
      window.history.replaceState({}, document.title, `${location.pathname}?${params}`)
    },
    handleSortingChange() {
      this.addSortingToUrl(this.selectedSortingOption)
      this.fetchSearchResults()
    },
    handleCategoryChange(category) {
      this.addCategoryToUrl(category.name)
      this.fetchSearchResults()
    },
    handleSearchSubmit(searchStringFromInputField) {
      this.addSearchQueryToUrl(searchStringFromInputField)
      this.fetchSearchResults(searchStringFromInputField)
    }
  }
}
</script>
